import Clickable from 'components/Common/Clickable'
import ModalContext from 'contexts/ModalContext'
import withMountGuard from 'hocs/withMountGuard'
import { useAppSelector } from 'hooks/reduxHooks'
import { trackEvent } from 'analytics/analytics'
import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import { loyaltyContext } from 'analytics/snowplow/eventContexts/loyaltyContext'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import type { MouseEventHandler } from 'react'
import { useCallback, useContext, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import LoyaltyStayUpgradeBenefitModal from './LoyaltyStayUpgradeBenefitModal'
import LoyaltyTierBenefitMessageBanner from './LoyaltyTierBenefitMessageBanner'
import styled from 'styled-components'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import OfferUrlContext from 'contexts/OfferUrlContext'

const ClickableContainer = styled(Clickable)`
  display: contents;
`

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary
  upgradeNotAvailableForSelectedDates?: boolean
  /**
   * Override passed through to the modal's package-upgrades card. SPP/LPC tiles pass `true`.
   * We only know answer with dates so doesn't make sense to store data on an offer level.
   **/
  showPackageUpgrades?: boolean
  /**
   * Override passed through to the modal's room-upgrades card. SPP/LPC tiles pass `false`.
   * We only know answer with dates so doesn't make sense to store data on an offer level.
   **/
  showRoomUpgrades?: boolean
}

function LoyaltyStayUpgradeBenefitMessageBanner({ offer, upgradeNotAvailableForSelectedDates = false, showPackageUpgrades, showRoomUpgrades }: Props) {
  const intl = useIntl()
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const showModal = useContext(ModalContext)
  const offerUrl = useContext(OfferUrlContext)

  const showRoomUpgradesModal = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    showModal(<LoyaltyStayUpgradeBenefitModal offer={offer} offerUrl={offerUrl} showPackageUpgrades={showPackageUpgrades} showRoomUpgrades={showRoomUpgrades} />)
  }, [showModal, offer, offerUrl, showPackageUpgrades, showRoomUpgrades])

  const loyaltyAccount = useAppSelector(state => state.loyalty.account.data)
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const trackedImpressionOfferIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (upgradeNotAvailableForSelectedDates || trackedImpressionOfferIdRef.current === offer.id) return
    trackedImpressionOfferIdRef.current = offer.id
    const upgradeType = showPackageUpgrades && showRoomUpgrades === false ? 'package' : undefined
    trackEvent({
      context: [
        comluxgroup.createItem_1_1_1({
          offerId: offer.id,
          categoryId: 'hotel',
          productId: isBedbankOffer(offer) ? 'bedbank' : OFFER_TYPE_ALWAYS_ON,
          currency,
          metadata: JSON.stringify({ loyaltyUpgradeBanner: true, ...(upgradeType && { upgradeType }) }),
        }),
        loyaltyContext(loyaltyAccount),
      ],
      event: comluxgroup.createProductImpression_1_0_3({}),
      options: { contextsToSkip: ['item'] },
    })
  }, [offer, upgradeNotAvailableForSelectedDates, showPackageUpgrades, showRoomUpgrades, loyaltyAccount, currency])

  const mobileUpgradeText = intl.formatMessage(
    {
      id: 'EZ_TIXE8TyloChfJMwch',
      defaultMessage: 'Free upgrade with your {tierName} status',
    },
    { tierName: getLoyaltyTierDisplayName(tier) },
  )

  let desktopUpgradeText: string
  if (tier === 'infinite') {
    desktopUpgradeText = intl.formatMessage(
      {
        id: 'U5Ka2OsRjbZZiqbbysme',
        defaultMessage: 'Enjoy a free upgrade with your {tierName} status',
      },
      { tierName: getLoyaltyTierDisplayName(tier) },
    )
  } else {
    desktopUpgradeText = intl.formatMessage(
      {
        id: 'VHt5Mm_en8enepgoNICZ',
        defaultMessage: 'Enjoy a free upgrade with your {tierName} {programName} status',
      },
      { tierName: getLoyaltyTierDisplayName(tier), programName: getLoyaltyProgramDisplayName() },
    )
  }
  const unavailableText = intl.formatMessage({
    id: 'fUT-M1cORt9rtixjj0AO',
    defaultMessage: 'Upgrades unavailable on selected dates',
  })

  return <ClickableContainer onClick={showRoomUpgradesModal}>
    <LoyaltyTierBenefitMessageBanner
      tier={tier}
      benefitType="hotel_upgrade"
      endContent={<LineInfoCircleIcon />}
      density="compact"
    >
      {
        !upgradeNotAvailableForSelectedDates &&
        <>
          <CSSBreakpoint max="tablet">
            {mobileUpgradeText}
          </CSSBreakpoint>
          <CSSBreakpoint min="desktop">
            {desktopUpgradeText}
          </CSSBreakpoint>
        </>
      }
      {
        upgradeNotAvailableForSelectedDates &&
        <>{unavailableText}</>
      }
    </LoyaltyTierBenefitMessageBanner>
  </ClickableContainer>
}

export default withMountGuard(() => {
  return useAppSelector(getIsLoyaltyEnabled)
})(LoyaltyStayUpgradeBenefitMessageBanner)
